/* eslint-disable no-console */
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import moment from 'moment';

const isTest = process.env.NODE_ENV === 'test';

/**
 * The locales currently supported by the app are specified in /config/shared/locales.json
 * Add a language there if you want to support a new translation.
 * Read more at /config/shared/README.md
 */

/**
 * translations are specified in /public/locales/{language-code}/translation.json
 */

/**
 * Ensures RTL CSS stylesheet has been loaded into the page.
 *
 * @returns {Promise} Resolves once loaded
 */
export function loadRtlCSS() {
  return new Promise(resolve => {
    const id = 'rtl-style';

    // Check if RTL style has already been loaded
    if (document.getElementById(id)) {
      return resolve();
    }

    const link = document.createElement('link');
    link.href = `assets/main.rtl.css?c=${window?.settings?.commit ?? ''}`;
    link.id = id;
    link.rel = 'stylesheet';
    link.type = 'text/css';

    link.onload = () => {
      link.onload = null;
      resolve();
    };

    document.head.append(link);
  });
}

/**
 * Format a translation parameter
 *
 * @param {Date|any} value - value to format
 * @param {string} format - a code that defines what to do with the string
 *    allowed values for Date:
 *      - fromNow - how long time has passed since the Date
 *      - age - amount of finished years since the Date
 *      - {format} - i.e. YYYYMMdd - format string to apply on the Date object
 * @param {string} languageCode - the ietf language code (learn more @/config/client/i18n-languages)
 * @returns {string|any} - the formatted value
 *                         or the original value, if the value type or format not recognized)
 */
function format(value, format, languageCode) {
  if (value instanceof Date) {
    moment.locale(languageCode);
    if (format === 'fromNow') return moment(value).fromNow();
    if (format === 'age') return moment().diff(moment(value), 'years');
    return moment(value).format(format);
  }

  if (typeof value === 'number' && format === 'number') {
    return value.toLocaleString(languageCode);
  }

  return value;
}

if (!isTest) {
  // load translation using Fetch via backend API
  // learn more: https://github.com/i18next/i18next-http-backend
  i18n.use(HttpApi);
}

// Get user locale from database (if available) before initialization
// This takes priority over cookie/browser language for logged-in users
let userLocaleFromDB = null;
if (typeof window !== 'undefined' && window.user && window.user.locale) {
  userLocaleFromDB = window.user.locale.trim();
  if (userLocaleFromDB) {
    console.log('[i18n init] User locale from database:', userLocaleFromDB);
  }
}

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    ...(isTest
      ? {
          resources: {
            en: {},
          },
        }
      : {}),
    fallbackLng: 'en', // a default app locale
    // allow keys to be phrases having `:`, `.`
    nsSeparator: false,
    keySeparator: false, // we do not use keys in form messages.welcome
    returnEmptyString: false,
    interpolation: {
      escapeValue: false, // react already safes from xss
      format,
    },
    detection: {
      lookupCookie: 'i18n',
      // If user has locale in database, use it first, otherwise try cookie, then browser
      order: userLocaleFromDB
        ? ['querystring', 'cookie', 'navigator']
        : ['cookie', 'navigator'],
      caches: ['cookie'], // Save to cookie when language changes
      cookieMinutes: 60 * 24 * 365, // 1 year
      cookieOptions: {
        path: '/',
        sameSite: 'lax',
      },
    },
    // Override detected language with user's database locale if available
    // This ensures logged-in users get their saved language preference from database
    ...(userLocaleFromDB ? { lng: userLocaleFromDB } : {}),
    react: {
      useSuspense: false,
    },
    backend: !isTest
      ? {
          // Fetch translations from server, which merges DB + static JSON
          loadPath: '/api/i18n/locales/{{lng}}/{{ns}}',
          // Reload translations when they change
          reloadInterval: false,
          // Allow cross-origin requests
          crossDomain: false,
          // Handle 404 errors gracefully (return empty object to allow fallback)
          allowMultiLoading: false,
        }
      : undefined,
    // debug: true, // show missing translation keys in console.log
  });

// Immediately after init, check if cookie was read correctly
// LanguageDetector should have read it during init, but verify it did
// This is critical for page refresh - the cookie must be read and applied
if (!isTest && typeof window !== 'undefined') {
  // Check immediately after init (init is synchronous)
  const checkCookieAfterInit = () => {
    try {
      // Check user locale from database first (takes priority)
      const userLocale =
        window.user && window.user.locale ? window.user.locale.trim() : null;
      const cookieMatch = document.cookie.match(/i18n=([^;]+)/);
      const cookieLanguage = cookieMatch ? cookieMatch[1].trim() : null;

      console.log(
        '[i18n init check] User locale from DB:',
        userLocale,
        'Cookie:',
        cookieLanguage,
        'i18n.language:',
        i18n.language,
        'isInitialized:',
        i18n.isInitialized,
      );

      // If user has locale in database and it differs, change it (this should already be set, but verify)
      if (userLocale && userLocale !== i18n.language) {
        console.log(
          `[i18n init check] User locale from DB ${userLocale} differs from i18n language ${i18n.language} - changing immediately...`,
        );
        i18n.changeLanguage(userLocale).catch(err => {
          console.error(
            '[i18n init check] Failed to change language from DB locale:',
            err,
          );
        });
      } else if (
        cookieLanguage &&
        cookieLanguage !== i18n.language &&
        !userLocale
      ) {
        // If cookie exists and differs, and no DB locale, change it
        console.log(
          `[i18n init check] Cookie language ${cookieLanguage} differs from i18n language ${i18n.language} - changing immediately...`,
        );
        i18n.changeLanguage(cookieLanguage).catch(err => {
          console.error(
            '[i18n init check] Failed to change language from cookie:',
            err,
          );
        });
      } else if (userLocale && userLocale === i18n.language) {
        console.log('[i18n init check] Language matches user DB locale - OK');
      } else if (cookieLanguage && cookieLanguage === i18n.language) {
        console.log('[i18n init check] Language matches cookie - OK');
      } else if (!cookieLanguage && !userLocale) {
        console.log(
          '[i18n init check] No cookie or DB locale found - will use current i18n.language:',
          i18n.language,
        );
      }
    } catch (err) {
      console.error('[i18n init check] Error:', err);
    }
  };

  // Check immediately (init is synchronous, but cookie reading might happen async in LanguageDetector)
  checkCookieAfterInit();

  // Also check after a tiny delay to catch any async cookie reading
  setTimeout(checkCookieAfterInit, 10);
  setTimeout(checkCookieAfterInit, 100);
}

// After initialization, verify cookie is read and apply if needed
// This ensures language persists on page reload
if (!isTest && typeof window !== 'undefined') {
  // Function to sync language from database/cookie to i18n
  const syncCookieLanguage = () => {
    try {
      // Check user locale from database first (takes priority for logged-in users)
      const userLocale =
        window.user && window.user.locale ? window.user.locale.trim() : null;
      // Check cookie as fallback
      const cookieMatch = document.cookie.match(/i18n=([^;]+)/);
      const cookieLanguage = cookieMatch ? cookieMatch[1].trim() : null;

      // Determine target language: DB locale > Cookie
      const targetLanguage = userLocale || cookieLanguage;

      console.log(
        'Language sync check - User DB locale:',
        userLocale,
        'Cookie:',
        cookieLanguage,
        'i18n.language:',
        i18n.language,
        'Target:',
        targetLanguage,
      );

      if (targetLanguage && targetLanguage !== i18n.language) {
        console.log(
          `Target language ${targetLanguage} differs from i18n language ${i18n.language} - syncing...`,
          userLocale ? '(from database)' : '(from cookie)',
        );
        i18n.changeLanguage(targetLanguage).catch(err => {
          console.error('Failed to change language:', err);
        });
      } else if (!targetLanguage && i18n.language && i18n.language !== 'en') {
        // If no DB locale or cookie but i18n has a non-English language, save it to cookie
        console.log(
          'No DB locale or cookie found, saving current language to cookie:',
          i18n.language,
        );
        const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
        document.cookie = `i18n=${i18n.language}; path=/; max-age=${maxAge}; SameSite=Lax`;
      }
    } catch (err) {
      console.error('Error syncing language:', err);
    }
  };

  // Check when i18n is ready - this fires after init completes
  i18n.on('initialized', () => {
    console.log('i18n initialized event fired, checking cookie...');
    syncCookieLanguage();
  });

  // Multiple fallback checks to ensure we catch the cookie
  setTimeout(syncCookieLanguage, 50);
  setTimeout(syncCookieLanguage, 200);
  setTimeout(syncCookieLanguage, 500);

  // Also check when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      console.log('DOM loaded, checking cookie...');
      syncCookieLanguage();
    });
  } else {
    syncCookieLanguage();
  }

  // Check on page visibility change (for SPA navigation)
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      console.log('Page restored from cache, checking cookie...');
      syncCookieLanguage();
    }
  });

  // Check on every page load/refresh
  window.addEventListener('load', () => {
    console.log('Window loaded, checking cookie...');
    syncCookieLanguage();
  });
}

i18n.on('languageChanged', async languageCode => {
  const direction = i18n.dir(languageCode); // `rtl` (right-to-left), or `ltr` (left-to-right)

  document.documentElement.lang = languageCode;
  document.documentElement.dir = direction;

  if (direction === 'rtl') {
    await loadRtlCSS();
  }

  // Date+time library
  moment.locale(languageCode);

  // Explicitly save language to cookie to ensure persistence
  // LanguageDetector should do this automatically, but we ensure it here too
  if (typeof document !== 'undefined') {
    const cookieName = 'i18n';
    const cookieValue = languageCode;
    const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
    const cookieString = `${cookieName}=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
    document.cookie = cookieString;
    console.log(
      '[i18n languageChanged] Language changed to',
      languageCode,
      '- cookie saved',
    );

    // Verify cookie was set
    const verifyCookie = document.cookie.match(/i18n=([^;]+)/);
    if (verifyCookie && verifyCookie[1] === languageCode) {
      console.log('[i18n languageChanged] Cookie verified:', verifyCookie[1]);
    } else {
      console.warn(
        '[i18n languageChanged] WARNING: Cookie may not have been set correctly',
      );
    }
  }

  // Force reload of all loaded namespaces for the new language
  // This ensures translations are properly loaded when language changes
  try {
    const loadedNamespaces = i18n.options.ns || ['translation'];
    const namespacesToReload = Array.isArray(loadedNamespaces)
      ? loadedNamespaces
      : [loadedNamespaces];

    // Reload each namespace to ensure fresh translations are loaded
    await Promise.all(
      namespacesToReload.map(ns =>
        i18n.reloadResources(languageCode, ns).catch(err => {
          // If a namespace fails to load, log but don't fail the whole operation
          console.warn(
            `Failed to reload namespace ${ns} for language ${languageCode}:`,
            err,
          );
        }),
      ),
    );
  } catch (err) {
    // Don't fail language change if reload fails
    console.warn('Error reloading translations:', err);
  }
});

// Ensure language persists across page navigations
// Check cookie on visibility change and sync if needed
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      // Page became visible - check if cookie language matches i18n language
      const cookieMatch = document.cookie.match(/i18n=([^;]+)/);
      const cookieLanguage = cookieMatch ? cookieMatch[1] : null;

      if (cookieLanguage && cookieLanguage !== i18n.language) {
        console.log('Syncing i18n language to cookie:', cookieLanguage);
        i18n.changeLanguage(cookieLanguage);
      }
    }
  });

  // Also check on page focus in case of tab switching
  window.addEventListener('focus', () => {
    const cookieMatch = document.cookie.match(/i18n=([^;]+)/);
    const cookieLanguage = cookieMatch ? cookieMatch[1] : null;

    if (cookieLanguage && cookieLanguage !== i18n.language) {
      console.log('Syncing i18n language to cookie on focus:', cookieLanguage);
      i18n.changeLanguage(cookieLanguage);
    }
  });
}

export default i18n;
